import React, { useCallback, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import CustomText from 'components/atoms/CustomText'
import { useAppThunkDispatch, useAppSelector } from 'hooks/redux'
import { subscribeInvestment, rescueInvestment, getInvestmentClient } from 'store/investments/thunks'
import { InvestmentOrderType, InvestmentStatus } from 'store/investments/types'
import { setShowDrawerError, setShowDrawerSuccess } from 'store/modals/actions'
import { parseDate } from 'utils/common'
import {
  Wrapper,
  ContentBox,
  Row,
  Column,
  ActionsBox,
  TitleSection,
  DisclaimerText,
  BackButton,
  ConfirmButton,
  LoadingIcon,
  Bold,
  styles
} from './styles'

type InvestmentShape = {
  date?: string
  amount?: string | number
  account?: string
  type?: string
  statusId?: number
}

type LocationState = {
  investment?: InvestmentShape
  fromInvestmentBalance?: boolean
  isTotalRescue?: boolean
}

const DetailInvestment: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppThunkDispatch()

  const { investment: investmentParam, isTotalRescue } = (location.state as LocationState) || {}

  const { investmentClient, investmentHours, isLoading } = useAppSelector(
    state => state.investments
  )

  const investment: InvestmentShape =
    investmentParam || {
      date: new Date().toISOString(),
      amount: '0',
      account: investmentClient?.Client?.Accounts?.[0]?.number || '',
      type: InvestmentOrderType.SUBSCRIPTION,
      statusId: InvestmentStatus.Pending
    }

  useEffect(() => {
    if ((!investmentParam || !investmentParam.account) && !investmentClient) {
      dispatch(getInvestmentClient())
    }
  }, [dispatch, investmentParam, investmentClient])

  const isSubscription =
    investment.type === InvestmentOrderType.SUBSCRIPTION ||
    investment.type === 'SUBSCRIPTION'

  const handleConfirm = useCallback(async () => {
    const amount = Number(investment.amount)

    const success = isSubscription
      ? await dispatch(subscribeInvestment(amount))
      : await dispatch(rescueInvestment(amount, Boolean(isTotalRescue)))

    const successTitle = '¡Listo!'
    const successBody = isSubscription
      ? 'En breve, tu dinero será invertido. Te avisaremos cuando esté todo listo.'
      : 'Rescataste en QIRA Pagos.'

    const errorTitle = 'Ocurrió un error.'
    const errorBody = 'Por favor, volvé a intentarlo.'

    if (success) {
      const operationLabel = isSubscription ? 'Suscripción' : 'Rescate'
      dispatch(
        setShowDrawerSuccess(true, true, successTitle, successBody, {
          path: '/content/investments',
          title: operationLabel,
          fromScreen: 'investments'
        })
      )
    } else {
      dispatch(
        setShowDrawerError(true, errorTitle, errorBody, {
          path: location.pathname,
          title: '',
          fromScreen: 'investments'
        }, 'Reintentar', () => {
          handleConfirm()
        })
      )
    }
  }, [dispatch, investment.amount, isSubscription, isTotalRescue, location.pathname])

  const formattedDate = parseDate(investment.date as string | Date, 'dd/MM/yyyy') || ''

  const headerLabel = isSubscription ? 'la suscripción' : 'el rescate'
  const dateLabel = isSubscription ? 'suscripción' : 'rescate'
  const accountLabel = isSubscription ? 'débito' : 'crédito'
  const confirmLabel = `Confirmar ${headerLabel}`
  const numericAmountValue = Number(investment.amount) || 0
  const amountDisplay = `$${numericAmountValue.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
  const subscriptionHoursText = investmentHours?.subscription?.join(' a ') || ''
  const rescueHoursText = investmentHours?.rescue?.join(' a ') || ''

  return (
    <Wrapper>
      <ContentBox>
        <TitleSection>
          <CustomText size="mmbig" weight="mediumfont" customStyle={styles.titleText}>
            {`Revisá el detalle de ${headerLabel}`}
          </CustomText>
        </TitleSection>

        <DisclaimerText>
          <CustomText
            size="small"
            weight="regular"
            customStyle={styles.disclaimerText}
          >
            Las suscripciones pueden realizarse de
            {' '}
            <Bold>
              Lunes a Viernes de
              {' '}
              {subscriptionHoursText}
              {' '}
              h
            </Bold>
            . Los rescates pueden realizarse de
            {' '}
            <Bold>
              Lunes a Viernes de
              {' '}
              {rescueHoursText}
              {' '}
              h
            </Bold>
            .
          </CustomText>

          <CustomText size="small" weight="regular" customStyle={styles.disclaimerText}>
            No disponibles en feriados.
          </CustomText>
        </DisclaimerText>
        <Row>
          <Column>
            <CustomText size="small" weight="regular" customStyle={styles.labelText}>
              {`Fecha de ${dateLabel}`}
            </CustomText>
            <CustomText size="big" weight="regular" customStyle={styles.valueText}>
              {formattedDate}
            </CustomText>
          </Column>

          <Column>
            <CustomText size="small" weight="regular" customStyle={styles.labelText}>
              {`Cuenta ${accountLabel}`}
            </CustomText>
            <CustomText size="big" weight="regular" customStyle={styles.valueText}>
              {investment.account || investmentClient?.Client?.Accounts?.[0]?.number || '—'}
            </CustomText>
          </Column>

          <Column>
            <CustomText size="small" weight="regular" customStyle={styles.labelText}>
              Monto
            </CustomText>
            <CustomText size="big" weight="regular" customStyle={styles.valueText}>
              {amountDisplay}
            </CustomText>
          </Column>
        </Row>

        <ActionsBox>
          <BackButton type="button" onClick={() => navigate(-1)}>
            <CustomText size="mmbig" weight="mediumfont" customStyle={styles.backButtonText}>
              Cancelar
            </CustomText>
          </BackButton>

          <ConfirmButton
            type="button"
            $isLoading={isLoading}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading
              ? (
                <LoadingIcon />
                )
              : (
                <CustomText size="mmbig" weight="mediumfont" customStyle={styles.confirmButtonText}>
                  {confirmLabel}
                </CustomText>
                )}
          </ConfirmButton>
        </ActionsBox>
      </ContentBox>
    </Wrapper>
  )
}

export default DetailInvestment
